import { defineComponent as _defineComponent } from 'vue'

const __default__ = {}

import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { __, n__ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzBadge, { OzBadgeColorScheme, OzBadgeSizePreset } from '@@/library/v4/components/OzBadge.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzRelativeTime from '@@/library/v4/components/OzRelativeTime.vue'
import type { Library, WallCamelCase as Wall, Wall as WallSnakeCase } from '@@/types'
import { computed, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'OzWallCard',
  props: {
    wall: null,
    cardWidth: null,
    wallTimestampType: { default: 'updatedAt' },
    xMoreButton: { type: Boolean, default: true },
    invisibleMoreButton: { type: Boolean, default: false },
    hrefTarget: { default: '_blank' },
    library: { default: undefined },
    canSelect: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false }
  },
  emits: ["more", "right-click", "select-card", "click"],
  setup(__props: any, { emit }: { emit: ({
  (name: 'more', wall: Wall | WallSnakeCase, event: PointerEvent): void
  (name: 'right-click', wall: Wall | WallSnakeCase, event: PointerEvent): void
  (name: 'select-card', wall: Wall | WallSnakeCase, event: MouseEvent | KeyboardEvent): void
  (name: 'click'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { wall: Wall | WallSnakeCase, cardWidth: number, wallTimestampType: string, xMoreButton: boolean, invisibleMoreButton: boolean, hrefTarget: string, library: Library, canSelect: boolean, isSelected: boolean };





const isSafari = device.safari

const isFocused = ref<boolean>(false)
const isHoveringMoreButton = ref<boolean>(false)

const handleMoreClick = (event: PointerEvent): void => {
  emit('more', props.wall, event)
  trackEvent('Dashboard', 'Clicked More button on dashboard wall card')
}

const wallLinkLabel = computed((): string => {
  return __('Go to %{wallTitle} padlet', { wallTitle: wallTitle.value })
})

const wallThumbnailTitle = computed((): string => {
  return __('%{wallTitle} padlet thumbnail', { wallTitle: wallTitle.value })
})

const wallTitle = computed((): string => {
  return props.wall.title || __('Untitled padlet')
})

const wallTimestamp = computed((): string => {
  if (props.wallTimestampType === 'updatedAt') {
    return 'updated_at' in props.wall ? props.wall.updated_at : props.wall.updatedAt
  }
  return props.wall[props.wallTimestampType]
})

const accountDisplayName = computed((): string => {
  const displayName =
    'displayName' in props.wall.builder ? props.wall.builder.displayName : props.wall.builder.display_name
  return props.library ? props.library.name : displayName
})

const daysTillWallIsDeleted = computed((): number => {
  const daysLeft =
    'daysTillDeletion' in props.wall ? props.wall.daysTillDeletion : (props.wall as WallSnakeCase).days_till_deletion
  return daysLeft ?? 0
})

const daysTillDeletedDisplay = computed((): string => {
  return n__('%{num} day left', '%{num} days left', daysTillWallIsDeleted.value, { num: daysTillWallIsDeleted.value })
})

const wallDeletingSoon = computed((): boolean => {
  return daysTillWallIsDeleted.value <= 3
})

const xRelativeTimestamp = computed((): boolean => {
  return props.wallTimestampType !== 'trashedAt'
})

const xTemplate = computed((): boolean => {
  if ('isTemplate' in props.wall) {
    return props.wall.isTemplate === true
  }
  return false
})

const moreButtonTopOffset = computed((): number => {
  const wallThumbnailHeight = props.cardWidth / 2 // Since the wall thumbnail has an aspect ratio of 2, the height of the wall thumbnail is half of its width
  const gapBetweenWallThumbnailAndWallTitle = 12 // 12px = gap between thumbnail and wall name
  return wallThumbnailHeight + gapBetweenWallThumbnailAndWallTitle
})

const href = computed((): string | undefined => {
  if (props.canSelect) return undefined
  return (props.wall.links && props.wall.links.show) || `/${props.wall.address}`
})

const showMoreMenu = (e: PointerEvent): void => {
  if (props.xMoreButton) {
    e.preventDefault()
    emit('right-click', props.wall, e)
    trackEvent('right-click', { target: 'dashboard-wall-card' })
  }
}

const handleWallThumbnailLoadingError = (e: Error): void => {
  // do nothing
  /**
   * #WONTFIX
   * Image load errors should be gone when they refresh the dashboard.
   * If users still don't see the image, they will probably email us with the padlet link, then we can get the image source by visiting the padlet
   *  Logging is error is not particularly helpful when there are so many of them.
   */
}

const navigateToPadletUrl = (): void => {
  navigateTo((props.wall.links && props.wall.links.show) || `/${props.wall.address}`, { target: props.hrefTarget })
}

const handleClick = (event: MouseEvent): void => {
  // If the user performs a ctrl/command/shift + click, we select the card
  if (event.ctrlKey || event.metaKey || event.shiftKey) {
    handleSelect(event)
    return
  }

  navigateToPadletUrl()
  emit('click')
}

const handleSelect = (event: MouseEvent): void => {
  if (props.canSelect) {
    emit('select-card', props.wall, event)
  }
}

const handleKeydownSpace = (event: KeyboardEvent): void => {
  // When OzBaseButton is rendered as a button, the space button is used to select/un-select the padlet
  if (props.canSelect) {
    emit('select-card', props.wall, event)
    return
  }

  navigateToPadletUrl()
}

const openPadletInNewTab = (event: PointerEvent): void => {
  // See https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
  const isMousewheelBeingClicked = event.button === 1
  if (isMousewheelBeingClicked) {
    window.open((props.wall.links && props.wall.links.show) || `/${props.wall.address}`, '_blank')
  }
}

return { __sfc: true,props, emit, isSafari, isFocused, isHoveringMoreButton, handleMoreClick, wallLinkLabel, wallThumbnailTitle, wallTitle, wallTimestamp, accountDisplayName, daysTillWallIsDeleted, daysTillDeletedDisplay, wallDeletingSoon, xRelativeTimestamp, xTemplate, moreButtonTopOffset, href, showMoreMenu, handleWallThumbnailLoadingError, navigateToPadletUrl, handleClick, handleSelect, handleKeydownSpace, openPadletInNewTab, __, ImageThumbnail, OzBadge, OzBadgeColorScheme, OzBadgeSizePreset, OzBaseButton, OzIcon, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset, OzRelativeTime }
}

})